import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
    Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel,
    ListSubheader, MenuItem, Select, TextField, Typography,
} from '@material-ui/core';
import { DisabledFormWrapper, CLUSTER_STATES } from 'app/main/util/clusterUtils';

import * as formHelpers from './formHelpers';
import {
    shouldEnableProximityPlacement, imageRoles,
    azureImageOptionState, azureStackOptionState,
    unavailableShapeReason, imageReason,
} from './formHelpers';
import { resolveMarketplaceAzureImage, fetchAzureTerms, createWindowsDesktopCluster, updateWindowsDesktopCluster } from '../store/actions/cluster.actions';
import * as ActionsDialog from 'app/store/actions';
import useClusterForm from '../hooks/useClusterForm';
import useAzureCatalog from './useAzureCatalog';
import usePinScopeReset from './usePinScopeReset';
import AzureImageChips from './AzureImageChips';
import AzureTermsPrompt from './AzureTermsPrompt';
import DeleteImageDialog from './DeleteImageDialog';
import ClusterFormHeader from '../components/ClusterFormHeader';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import BasicInformationSection from '../components/BasicInformationSection';
import ImageSelectionSection from '../components/ImageSelectionSection';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration';
import StorageSettingsSection from '../components/StorageSettingsSection';
import NetworkSettingsSection from '../components/NetworkSettingsSection';
import DescriptionSection from '../components/DescriptionSection';
import CredentialSelector from '../components/CredentialSelector';
import RegionSelector from '../components/RegionSelector';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import CapacityReservationsSection from '../components/CapacityReservationsSection';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';

const stackTypeChipStyle = { fontSize: 11, height: 20 };

const AzureCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, formInvalidReason, volSizeLimits,
        quotas, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({
        provider: 'azure', props, formHelpers,
        extraValidation: (f) => (f.imageResourceGroupName ? true : 'an image resource group name'),
        // The shared providers.json-based zone check is replaced by the live
        // catalog below, which knows what this subscription can actually use.
        availabilityOptions: { enabled: false },
    });

    const dispatch = useDispatch();

    // Feature: windows-desktop-vms - "Windows Desktop" is an entry in the same
    // Operating System picker every other Azure cluster uses (see
    // BasicInformationSection's extraOsOptions below), not a separate dialog.
    // It deploys through a completely different path though (no Heidi image
    // catalog, no compute node groups, no resource manager - a single Azure
    // Marketplace VM, see commands/cluster/azure-windows/create.js), so once
    // selected it swaps out the Linux-cluster sections below for a minimal
    // set of fields and posts to its own endpoint instead of saveCluster.
    const isWindowsDesktop = form?.osType === 'windows-desktop';
    // Mirrors the generic Linux path: a cluster can only be updated while it is
    // still staged (state 'down', never deployed) - commands/cluster/azure-windows/
    // has no update for an already-deployed VM (no running config to change it
    // through), only create.js/deploy.js/destroy.js. Once deployed/deploying/
    // failed, fields stay visible (so the admin can see what was deployed) but
    // read-only, and Update/Save is disabled.
    const isWindowsEdit = isWindowsDesktop && props.type === 'edit' && form?.state !== CLUSTER_STATES.DESTROYED;
    const [windowsSubmitting, setWindowsSubmitting] = useState(false);
    const [windowsError, setWindowsError] = useState(null);
    // Feature: windows-desktop-vms Stack Manager. 'sku:<value>' deploys the
    // stock Marketplace image directly (today's only option); 'stack:<id>'
    // deploys from a stack an admin already built - same RDP/RDSH setup
    // already baked in, so first boot is faster than the Marketplace image.
    const [windowsImageChoice, setWindowsImageChoice] = useState('sku:2022-datacenter-azure-edition');

    const azureStacks = useSelector(({ stacks }) => stacks.stack.list || []);
    const stacks = useMemo(
        () => azureStacks.filter((s) => s.provider === 'azure'),
        [azureStacks]
    );
    const stackByPrefix = useMemo(() => {
        const map = {};
        stacks.forEach((s) => { if (s.data && s.data.prefix) map[s.data.prefix] = s; });
        return map;
    }, [stacks]);

    // Feature: windows-desktop-vms Stack Manager. Reuses the same already-fetched
    // azure `stacks` list above - no separate fetch needed. Only a stack that
    // actually finished a unified build (unifiedImageId set) is deployable;
    // one still building or that never succeeded has no image to boot from yet.
    // Declared here (ahead of handleWindowsSubmit below, which depends on it)
    // rather than down with the rest of the stack/marketplace state.
    const windowsStacks = useMemo(
        () => stacks.filter((s) => s.data && typeof s.data.type === 'string'
            && s.data.type.startsWith('windows-server-') && s.unifiedImageId),
        [stacks]
    );

    // Editing a staged (not-yet-deployed) Windows cluster loads its saved
    // enableRdsh/image choice straight from `form` (see formHelpers.js's
    // savedAzureFields) - but windowsImageChoice is a derived UI-only string
    // ('sku:x' or 'stack:id'), so it needs a one-time resolve once the
    // cluster's data (and the stacks list, which may still be loading) are
    // both available. Without this, reopening an edit always showed the
    // picker's hardcoded default instead of what was actually chosen.
    useEffect(() => {
        if (!isWindowsDesktop || props.type !== 'edit' || !form?.id) return;
        if (form.stackImageName) {
            const match = windowsStacks.find((s) => s.data?.prefix === form.stackImageName);
            if (match) { setWindowsImageChoice(`stack:${match.id}`); return; }
        }
        if (form.windowsSku) setWindowsImageChoice(`sku:${form.windowsSku}`);
    }, [isWindowsDesktop, props.type, form?.id, form?.stackImageName, form?.windowsSku, windowsStacks]);

    const windowsMissing = useMemo(() => {
        const missing = [];
        if (!form?.displayName) missing.push('cluster name');
        if (!form?.credentialId) missing.push('credential');
        if (!form?.regionId) missing.push('region');
        if (!form?.serverInstanceId) missing.push('VM size');
        return missing;
    }, [form?.displayName, form?.credentialId, form?.regionId, form?.serverInstanceId]);
    const windowsFormValid = windowsMissing.length === 0 && !windowsSubmitting;
    const windowsInvalidText = isWindowsEdit
        ? 'Windows Desktop clusters cannot be edited - destroy and recreate instead.'
        : (windowsError
            || (windowsMissing.length ? `Complete the following before saving: ${windowsMissing.join(', ')}.` : undefined));

    const handleWindowsSubmit = useCallback(async () => {
        setWindowsSubmitting(true);
        setWindowsError(null);
        try {
            const isStackChoice = windowsImageChoice.startsWith('stack:');
            const chosenStack = isStackChoice
                ? windowsStacks.find((s) => s.id === windowsImageChoice.slice('stack:'.length))
                : null;
            const payload = {
                displayName: form.displayName,
                credentialId: form.credentialId,
                regionId: form.regionId,
                instanceId: form.serverInstanceId,
                enableRdsh: !!form.enableRdsh,
                // Mutually exclusive with the backend: either a stock Marketplace
                // SKU, or a built stack's image found by prefix (see
                // clusters/azure-windows/data.tf) - never both.
                windowsSku: isStackChoice ? undefined : windowsImageChoice.slice('sku:'.length),
                stackImageName: chosenStack ? chosenStack.data.prefix : undefined,
                stackImageResourceGroupName: chosenStack ? chosenStack.data.resource_group_name : undefined,
            };
            if (props.type === 'edit' && form.id) {
                await dispatch(updateWindowsDesktopCluster(form.id, payload));
            } else {
                await dispatch(createWindowsDesktopCluster(payload));
            }
            dispatch(ActionsDialog.closeDialog());
        } catch (err) {
            setWindowsError((err.response && err.response.data && err.response.data.message) || err.message || `Failed to ${props.type === 'edit' ? 'update' : 'create'} the cluster`);
        } finally {
            setWindowsSubmitting(false);
        }
    }, [dispatch, form, windowsImageChoice, windowsStacks, props.type]);

    const catalog = useAzureCatalog({
        credentialId: form?.credentialId,
        regionId: form?.regionId,
    });

    // A pinned image belongs to one subscription and region.
    usePinScopeReset(form, setField);

    const [deleteRow, setDeleteRow] = useState(null);

    const credentials = useSelector(({ credentials: c }) => c.credential.list || []);
    const credentialsById = useMemo(() => {
        const map = {};
        credentials.forEach((c) => { map[c._id] = c.name; });
        return map;
    }, [credentials]);
    const credentialName = credentialsById[form?.credentialId] || null;

    // --- Marketplace ------------------------------------------------------
    const mpConfig = marketplace?.config || {};
    const isMarketStack = Boolean(marketplace?.isMarketStack);
    const plan = isMarketStack && mpConfig.publisher && mpConfig.offer && mpConfig.sku
        ? { publisher: mpConfig.publisher, offer: mpConfig.offer, plan: mpConfig.sku }
        : null;
    const requiresPlan = Boolean(mpConfig.requires_plan);

    const termsKey = plan && form?.credentialId
        ? [form.credentialId, plan.publisher, plan.offer, plan.plan].join('|')
        : null;
    const termsState = useSelector(({ clusters }) => clusters.cluster.azureCatalog.terms);
    const terms = termsKey ? termsState[termsKey] : null;

    // Only a plan-bearing image needs terms; a plan-free one deploys regardless.
    useEffect(() => {
        if (!requiresPlan || !plan || !form?.credentialId || termsKey === null) return;
        if (terms) return;
        dispatch(fetchAzureTerms(form.credentialId, plan));
    }, [dispatch, requiresPlan, termsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const credentialId = form?.credentialId;
    const recheckTerms = useCallback(() => {
        if (plan && credentialId) dispatch(fetchAzureTerms(credentialId, plan));
    }, [dispatch, plan, credentialId]);

    // accepted === false is a refusal; null means we could not ask, which warns
    // but never blocks.
    const termsBlocked = Boolean(requiresPlan && terms && terms.accepted === false);
    const termsForCompat = requiresPlan ? terms : null;
    const productName = marketplace?.title || mpConfig.offer || 'this Marketplace image';

    // Always-latest ParaTools image (Azure), mirroring the AWS resolve-ami flow:
    // when a marketplace variant with a publisher/offer/sku is selected and a
    // credential is chosen, resolve the NEWEST published version and pin it onto
    // the form's marketplace image reference. Without this the catalog's static
    // marketplace_image_version can go stale (deploy fails PlatformImageNotFound).
    // The pinned version then stays with the cluster after creation.
    useEffect(() => {
        if (!isMarketStack || !form?.credentialId || !plan) return;
        let cancelled = false;
        (async () => {
            const data = await dispatch(resolveMarketplaceAzureImage({
                credentialId: form.credentialId,
                publisher: plan.publisher, offer: plan.offer, sku: plan.plan,
                location: form.regionId,
            }));
            if (cancelled || !data || !data.version) return;
            setField({
                marketplaceImageReference: {
                    publisher: plan.publisher, offer: plan.offer, sku: plan.plan,
                    marketplace_image_version: data.version,
                    requires_plan: requiresPlan,
                },
            });
        })();
        return () => { cancelled = true; };
    }, [isMarketStack, mpConfig, form?.credentialId, form?.regionId, dispatch, setField]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Images -----------------------------------------------------------
    // The image the chosen sizes have to be able to boot: the pin if there is
    // one, otherwise whatever the stack name resolves to.
    const pinnedImageId = form?.serverImageId;
    const stackImageName = form?.serverImageName;
    const compatImage = useMemo(() => {
        if (!catalog.imagesReady) return null;
        return pinnedImageId
            ? catalog.imageById(pinnedImageId)
            : catalog.resolveImage(stackImageName, 'heidi');
    }, [catalog, pinnedImageId, stackImageName]);

    const cloudImages = useMemo(() => (
        (catalog.images || []).filter((img) => img.kind === 'heidi').map((img) => ({
            id: img.imageId,
            name: img.name,
            image: img,
        }))
    ), [catalog.images]);

    const cloudImagesPlaceholder = !form?.credentialId || !form?.regionId
        ? 'Select a credential and region first'
        : catalog.imageCatalog && catalog.imageCatalog.status !== 'ok'
            ? `Could not list images: ${catalog.imageCatalog.message}`
            : `No Heidi images in ${form.regionId} for this credential`;

    // --- Prices -----------------------------------------------------------
    const priceFor = useCallback((inst) => inst.price, []);

    /** Whole-cluster hourly estimate, or null when any price is unknown. */
    const clusterEstimate = useMemo(() => {
        if (!catalog.catalogReady) return null;
        let total = 0;
        for (const role of imageRoles(form)) {
            const price = catalog.priceOf(role.instance_id);
            if (price == null) return null;
            total += price * (role.count || 1);
        }
        return total > 0 ? total : null;
    }, [catalog, form]);

    // --- Validation safety nets ------------------------------------------
    const shapeReason = unavailableShapeReason(form, catalog);
    const imgReason = imageReason(form, catalog, { terms: termsForCompat, productName });
    const azureInvalidReason = useMemo(() => {
        const bits = [];
        if (shapeReason) bits.push(shapeReason);
        if (imgReason) bits.push(imgReason);
        if (termsBlocked) bits.push(`accepted Marketplace terms for ${productName}`);
        return bits.length ? bits.join(', and ') : null;
    }, [shapeReason, imgReason, termsBlocked, productName]);

    const azureFormValid = isFormValid && !azureInvalidReason;
    const azureInvalidText = azureInvalidReason
        ? `Complete the following before saving: ${azureInvalidReason}`
        : formInvalidReason;

    const headShapeHelper = useMemo(() => {
        if (!form?.serverInstanceId || !catalog.catalogReady) return undefined;
        const state = catalog.instanceOptionState(form.serverInstanceId, {
            image: compatImage, roles: imageRoles(form, 'head'), terms: termsForCompat, productName,
        });
        return state.disabled ? state.reason : undefined;
    }, [catalog, form, compatImage, termsForCompat, productName]);

    // --- Handlers ---------------------------------------------------------
    const handleServerInstanceChange = useCallback((instanceId) => {
        const updatedForm = { ...form, serverInstanceId: instanceId };
        setField({
            serverInstanceId: instanceId,
            useProximityPlacement: shouldEnableProximityPlacement(updatedForm),
        });
    }, [form, setField]);

    const handleComputeInstanceChange = useCallback((index, instanceId) => {
        const updatedGroups = [...(form?.nodeGroups || [])];
        updatedGroups[index] = { ...updatedGroups[index], instance_id: instanceId };
        const updatedForm = { ...form, nodeGroups: updatedGroups };
        setField({
            nodeGroups: updatedGroups,
            useProximityPlacement: shouldEnableProximityPlacement(updatedForm),
        });
    }, [form, setField]);

    const renderAzureGroupFields = useCallback((group, index, { updateGroup }) => (
        <ComputeNodeGroupFields
            group={group} index={index} updateGroup={updateGroup}
            provider="azure" imageMode={form?.imageMode} maxCount={250}
            onInstanceChange={handleComputeInstanceChange}
            allowedInstanceIds={marketplace.allowedComputeIds}
            disableStackFields={marketplace.disableStackFields}
            disabled={disableInputs}
            instanceOptions={catalog.shapes} instanceLoading={catalog.catalogLoading}
            getInstanceOptionState={(inst) => catalog.instanceOptionState(inst.instance_id, {
                image: compatImage, roles: imageRoles(form, index), terms: termsForCompat, productName,
            })}
            getInstancePrice={priceFor}
        />
    ), [form, marketplace, handleComputeInstanceChange, disableInputs, catalog, compatImage, termsForCompat, productName, priceFor]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="azure" providerLabel="Azure" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isWindowsDesktop ? (isWindowsEdit ? false : windowsFormValid) : azureFormValid}
                            formInvalidReason={isWindowsDesktop ? windowsInvalidText : azureInvalidText}
                            loading={loading || windowsSubmitting} disableInputs={disableInputs}
                            formType={props.type}
                            onSave={isWindowsDesktop ? handleWindowsSubmit : handleSubmit}
                            onEdit={isWindowsDesktop ? (isWindowsEdit ? undefined : handleWindowsSubmit) : handleEdit} onClose={handleClose}
                            subtitle={isWindowsDesktop
                                ? undefined
                                : (clusterEstimate ? `- Est. $${clusterEstimate.toFixed(2)}/hr` : undefined)}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs || isWindowsEdit}>
                            <div className="!w-full">

                                <BasicInformationSection
                                    form={form} setField={setField} provider="azure"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                    extraOsOptions={[{ value: 'windows-desktop', label: 'Windows Desktop' }]}
                                    hideImage
                                />

                                {isWindowsDesktop && (
                                    <SectionCard icon="desktop_windows" title="Windows Desktop (Azure)">
                                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                            {isWindowsEdit
                                                ? 'This cluster’s settings cannot be changed after creation. Destroy it and create a new one to change the credential, region or VM size.'
                                                : 'A single Windows Server VM, reached in the browser over RDP through Guacamole. Enable multi-session below for one server shared by several students (RDS); leave it off for a dedicated VM.'}
                                        </Typography>
                                        <div style={inputGroupStyle}>
                                            <CredentialSelector provider="azure" value={form.credentialId}
                                                onChange={(id) => setField('credentialId', id)} />
                                            <RegionSelector provider="azure" value={form.regionId || ''}
                                                onChange={(id) => setField('regionId', id)}
                                                options={catalog.regions} loading={catalog.regionsLoading}
                                                disabled={!form.credentialId} disabledHint="Select a Credential first"
                                                error={Boolean(catalog.regionsError)}
                                                helperText={catalog.regionsError
                                                    ? `Could not list regions from Azure: ${catalog.regionsError}`
                                                    : undefined}
                                            />
                                        </div>
                                        <div style={inputGroupStyle}>
                                            <InstanceSizeSelector provider="azure" value={form.serverInstanceId || ''}
                                                onChange={handleServerInstanceChange}
                                                label="VM Size"
                                                options={catalog.shapes} loading={catalog.catalogLoading}
                                                disabled={!form.regionId} disabledHint="Select a Region first"
                                                getPrice={priceFor}
                                            />
                                        </div>
                                        <div style={inputGroupStyle}>
                                            <FormControl fullWidth>
                                                <InputLabel id="windows-image-choice-label">Image</InputLabel>
                                                <Select
                                                    labelId="windows-image-choice-label"
                                                    value={windowsImageChoice}
                                                    onChange={(e) => setWindowsImageChoice(e.target.value)}
                                                >
                                                    <ListSubheader>Stock Marketplace images</ListSubheader>
                                                    <MenuItem value="sku:2019-datacenter">Windows Server 2019</MenuItem>
                                                    <MenuItem value="sku:2022-datacenter-azure-edition">Windows Server 2022</MenuItem>
                                                    <MenuItem value="sku:2022-datacenter-g2">Windows Server 2022 (Gen 2)</MenuItem>
                                                    {windowsStacks.length > 0 && [
                                                        <ListSubheader key="stacks-header">
                                                            Stacks (pre-baked, faster boot)
                                                        </ListSubheader>,
                                                        ...windowsStacks.map((s) => (
                                                            <MenuItem key={s.id} value={`stack:${s.id}`}>
                                                                {s.displayName || s.name}
                                                            </MenuItem>
                                                        )),
                                                    ]}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <FormControlLabel
                                            control={(
                                                <Checkbox
                                                    checked={!!form.enableRdsh}
                                                    onChange={(e) => setField('enableRdsh', e.target.checked)}
                                                />
                                            )}
                                            label="Enable multi-session (several students, one server)"
                                        />
                                    </SectionCard>
                                )}

                                {!isWindowsDesktop && termsBlocked && (
                                    <AzureTermsPrompt
                                        productName={productName}
                                        credentialName={credentialName}
                                        licenseTextLink={terms?.licenseTextLink}
                                        onRecheck={recheckTerms}
                                        checking={Boolean(terms?.loading)}
                                    />
                                )}

                                {/* Infrastructure + Image (two columns) */}
                                {!isWindowsDesktop && (
                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard icon="cloud" title="Infrastructure" fillHeight>
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="azure" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <RegionSelector provider="azure" value={form.regionId || ''}
                                                    onChange={(id) => setField('regionId', id)}
                                                    allowedRegionIds={marketplace.config?.allowed_regions?.length > 0
                                                        ? marketplace.config.allowed_regions : undefined}
                                                    options={catalog.regions} loading={catalog.regionsLoading}
                                                    disabled={!form.credentialId} disabledHint="Select a Credential first"
                                                    error={Boolean(catalog.regionsError) || catalog.regionUnusable}
                                                    helperText={catalog.regionsError
                                                        ? `Could not list regions from Azure: ${catalog.regionsError}`
                                                        : catalog.regionUnusable
                                                            ? `No VM sizes are available to this subscription in ${form.regionId}. Azure lists the region, but every size in it is restricted. Pick another region.`
                                                            : undefined}
                                                />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <InstanceSizeSelector provider="azure" value={form.serverInstanceId || ''}
                                                    onChange={handleServerInstanceChange}
                                                    allowedInstanceIds={marketplace.config?.instance_type}
                                                    label="Head Node Size"
                                                    options={catalog.shapes} loading={catalog.catalogLoading}
                                                    disabled={!form.regionId} disabledHint="Select a Region first"
                                                    getOptionState={(inst) => catalog.instanceOptionState(inst.instance_id, {
                                                        image: compatImage, roles: imageRoles(form, 'head'),
                                                        terms: termsForCompat, productName,
                                                    })}
                                                    getPrice={priceFor}
                                                    error={Boolean(headShapeHelper)}
                                                    helperText={headShapeHelper}
                                                />
                                                <TextField
                                                    className={clsx(classes.input)}
                                                    style={{ flex: 1 }}
                                                    label="Image Resource Group Name"
                                                    variant="outlined"
                                                    value={form.imageResourceGroupName || ''}
                                                    onChange={(e) => setField('imageResourceGroupName', e.target.value)}
                                                    placeholder="Exp: Image_group_1"
                                                />
                                            </div>
                                            {catalog.catalogError && (
                                                <Typography variant="body2" color="error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    Could not load {form.regionId} from Azure: {catalog.catalogError}
                                                    <Button size="small" variant="outlined" onClick={catalog.retry}>Retry</Button>
                                                </Typography>
                                            )}
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        {/* Image picker sits next to Infrastructure so the user fills
                                            credential + region first, then selects the image (as on AWS/IBM). */}
                                        <ImageSelectionSection
                                            form={form} setField={setField} provider="azure"
                                            disableStackFields={marketplace.disableStackFields}
                                            stacks={stacks}
                                            getStackOptionState={(stack) => azureStackOptionState(stack, {
                                                form, catalog, credentialsById, terms: termsForCompat, productName,
                                            })}
                                            renderStackExtras={(stack) => {
                                                const image = catalog.imagesReady
                                                    ? catalog.resolveImage(stack.data?.prefix, 'heidi')
                                                    : null;
                                                return (
                                                    <>
                                                        {stack.data?.type && <Chip size="small" style={stackTypeChipStyle} label={stack.data.type} />}
                                                        {image && <AzureImageChips image={image} />}
                                                    </>
                                                );
                                            }}
                                            cloudImages={isMarketStack ? [] : cloudImages}
                                            cloudImagesLoading={catalog.catalogLoading}
                                            cloudImagesPlaceholder={cloudImagesPlaceholder}
                                            getCloudImageOptionState={(img) => azureImageOptionState(img.image, {
                                                form, catalog, terms: termsForCompat, productName,
                                            })}
                                            renderCloudImageExtras={(img) => <AzureImageChips image={img.image} />}
                                            onDeleteCloudImage={(img) => setDeleteRow({
                                                prefix: img.image.prefix,
                                                image: img.image,
                                                stack: stackByPrefix[img.image.prefix] || null,
                                            })}
                                            imageNameFor={(img) => img.image.prefix}
                                            emptyLabel={isMarketStack ? (marketplace.title || 'Marketplace image') : undefined}
                                            error={Boolean(imgReason)}
                                            helperText={imgReason || undefined}
                                        />
                                    </div>
                                </div>
                                )}

                                {!isWindowsDesktop && (
                                <ResourceManagerConfiguration
                                    form={form} setField={setField}
                                    hideBursting={marketplace.isMarketStack}
                                    lockManager={Boolean(marketplace.config?.resource_manager)}
                                />
                                )}

                                {!isWindowsDesktop && (
                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="azure" advanced={advanced}
                                    marketplace={marketplace} disableInputs={disableInputs}
                                />
                                )}

                                {/* Advanced: Network + Capacity Reservations */}
                                {!isWindowsDesktop && advanced && (
                                    <div style={columnContainerStyle}>
                                        <div style={columnStyle}>
                                            <NetworkSettingsSection
                                                form={form} setField={setField} fillHeight
                                            />
                                        </div>
                                        <div style={columnStyle}>
                                            <CapacityReservationsSection
                                                form={form} setField={setField}
                                                fillHeight showAutoExpire
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Advanced: Storage Settings */}
                                {!isWindowsDesktop && advanced && (
                                    <StorageSettingsSection
                                        form={form} setField={setField} provider="azure"
                                        volSizeLimits={volSizeLimits}
                                    />
                                )}

                                {/* Compute Nodes */}
                                {!isWindowsDesktop && (
                                <ComputeNodesSection
                                    form={form} setField={setField}
                                    disabled={disableInputs}
                                    maxGroups={250}
                                    renderGroupFields={renderAzureGroupFields}
                                />
                                )}

                                {!isWindowsDesktop && <DescriptionSection form={form} setField={setField} />}

                                <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="sm" fullWidth>
                                    {deleteRow && (
                                        <DeleteImageDialog
                                            row={deleteRow}
                                            credentialId={form.credentialId}
                                            credentialName={credentialName}
                                            region={form.regionId}
                                            onClose={() => setDeleteRow(null)}
                                            onDeleted={() => {
                                                // A deleted image cannot stay pinned, and the catalog
                                                // still lists it until it is fetched again.
                                                if (deleteRow.image && form.serverImageId === deleteRow.image.imageId) {
                                                    setField('serverImageId', '');
                                                }
                                                catalog.retry();
                                            }}
                                        />
                                    )}
                                </Dialog>
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default AzureCluster;
